<template>
  <div v-click-away="onResetEditMode">
    <booking-bar-context
      v-if="editMode"
      ref="bookingBar"
      :hotel-name="hotelContext?.hotel?.name || ''"
      :minimum-stay-duration="
        // eslint-disable-next-line vue/no-deprecated-filter
        hotelContext?.hotel?.minimumStayDuration as number | undefined
      "
      :loading="loading"
      :empty="empty"
      :children-max-age="
        // eslint-disable-next-line vue/no-deprecated-filter
        hotelContext?.hotel?.maxChildAge as number | undefined
      "
      class="BookingContext-bar"
      :class="{ 'BookingContext-bar--editable': editMode }"
      @reset="onResetBookingBar"
      @submit="updateBooking" />

    <booking-context-read
      :hotel-name="hotelContext?.hotel?.name || ''"
      :rooms-and-guests="roomsAndGuests"
      class="BookingContext-read"
      :class="{ 'BookingContext-read--editable': editMode }"
      @edit="setEditMode" />
  </div>
</template>

<script setup lang="ts">
import { useApolloClient } from '@vue/apollo-composable';

import { directive as vClickAway } from '~/directives/click-away';
import type { BookingBarPayload, UnconfiguredGuests } from '~/domains/booking';
import { useBookingStore, useBookingUrlGenerator } from '~/domains/booking';
import {
  ValidateRoomsDocument,
  useBestOfferLazyQuery,
  useHotelContextQuery,
} from '~/domains/graphql';
import { extractRoomsAndGuestsFromStore } from '~/helpers/booking.helper';

import BookingBarContext from '../BookingBar/BookingBarContext.vue';

import BookingContextRead from './BookingContextRead.vue';

const route = useRoute();
const bookingStore = useBookingStore();
const { bookingURL } = useBookingUrlGenerator();
const { client } = useApolloClient();

const hotelId: any =
  bookingStore.hotelId ||
  (route.params.ridcode as string) ||
  route.query.ridcode ||
  '0';

const { result: hotelContext } = useHotelContextQuery(
  { hotelId },
  { fetchPolicy: 'cache-first' },
);

const editMode = ref(false);
const submitPayload = ref<BookingBarPayload>();
const bookingBar = ref<typeof BookingBarContext>();
const validateRoomsLoading = ref(false);
const validRooms = ref(true);

const setEditMode = ({ isMobile }: { isMobile: boolean }) => {
  editMode.value = true;
  if (isMobile) {
    nextTick(() => {
      openMobileForm();
    });
  }
};

const openMobileForm = () => {
  if (typeof bookingBar.value?.openMobileForm !== 'function') return;

  bookingBar.value.openMobileForm();
};

const roomsAndGuests = computed(() => {
  const guestsFromStore = extractRoomsAndGuestsFromStore(bookingStore);

  if (guestsFromStore.length <= 0) return [{ adults: 2, childrenAges: [] }];

  return guestsFromStore;
});

const adults = computed(() =>
  roomsAndGuests.value.reduce((sum, room) => sum + room.adults, 0),
);
const childrenAges = computed(() =>
  roomsAndGuests.value.flatMap(room => room.childrenAges),
);

const {
  onResult,
  result,
  load,
  loading: bestOfferLoading,
  document: bestOfferDocument,
} = useBestOfferLazyQuery(
  {
    adults: adults.value,
    childrenAges: childrenAges.value,
    dateIn: bookingStore.dates?.dateIn as string,
    hotelCode: hotelId,
    nights: bookingStore.dates?.nights,
  },
  { fetchPolicy: 'no-cache' },
);

onResult(gqlResult => {
  if (!submitPayload.value) return;

  if (gqlResult.data.bestOffer.empty) return;

  if (!editMode.value) return;

  if (typeof bookingBar.value?.closeMobileForm === 'function') {
    bookingBar.value.closeMobileForm();
  }

  const hotelId = bookingStore.hotelId as string;
  bookingStore.$reset();
  bookingStore.hotelId = hotelId;

  editMode.value = false;

  let bookingUrl = bookingURL(
    submitPayload.value.roomsAndGuests,
    hotelId,
    submitPayload.value.dateIn,
    submitPayload.value.nights,
  );

  const partner: any = route?.query?.partner_id;
  const origin: any = route?.query?.origin;

  if (partner) {
    bookingUrl += `&partner_id=${partner}`;
  }

  if (origin) {
    bookingUrl += `&origin=${origin}`;
  }

  // We force the push state to force the URL recomputing
  window.location.href = bookingUrl;
});

const validateRooms = async (
  dateIn: string,
  nights: number,
  roomsAndGuests: Array<UnconfiguredGuests>,
) => {
  validRooms.value = true;
  validateRoomsLoading.value = true;

  const validRoomsQuery = await client.query({
    query: ValidateRoomsDocument,
    variables: {
      dateIn,
      nights,
      hotelCode: hotelId,
      rooms: roomsAndGuests,
    },
    fetchPolicy: 'no-cache',
  });

  if (!validRoomsQuery.data.validateRooms) {
    validRooms.value = false;
  }

  validateRoomsLoading.value = false;

  return validRooms.value;
};

const updateBooking = async (payload: BookingBarPayload) => {
  submitPayload.value = payload;

  const result = await validateRooms(
    payload.dateIn,
    payload.nights,
    payload.roomsAndGuests,
  );

  if (result) {
    load(bestOfferDocument.value, {
      adults: payload.roomsAndGuests[0].adults,
      childrenAges: payload.roomsAndGuests[0].childrenAges,
      dateIn: payload.dateIn,
      hotelCode: hotelId,
      nights: payload.nights,
    });
  }
};

const loading = computed(() => {
  return bestOfferLoading.value || validateRoomsLoading.value;
});
const empty = computed(() => {
  return result?.value?.bestOffer?.empty || !validRooms.value;
});

const onResetEditMode = (mouseEvent: MouseEvent) => {
  if (bookingBar.value?.isMobileFormOpen) return;
  if (!editMode.value) return;

  const calendar = document.querySelector('#dp-menu-desktop-picker');
  const isInCalendar = calendar?.contains(mouseEvent.target as Node);

  if (isInCalendar) return;

  bookingBar.value?.resetBookingBarContext();
};

const onResetBookingBar = () => {
  editMode.value = false;
  validRooms.value = true;
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/themes/rixos/constants' as RixosTheme;

.BookingContext--desktop {
  display: none;

  @media (mq.$from-large) {
    display: inherit;
  }
}

.BookingContext--mobile {
  @media (mq.$from-large) {
    display: none;
  }
}

.BookingContext-bar {
  display: none;

  &--editable {
    display: inherit;
  }
}

.BookingContext-read {
  display: inherit;

  &--editable {
    display: none;
  }
}

.theme.rixos .AiBookingBar {
  :deep(.AiIcon) {
    color: RixosTheme.$color-primary-black;
  }

  :deep(.AiButton) {
    background: RixosTheme.$color-primary-black;
    border: 0;

    .AiTypo {
      color: colors.$white;
    }
  }
}
</style>
